import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, Lock } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const POINTS = [
  "Compliant with NSW EPA landfill bans.",
  "Priority on refurbishing and ethical reuse.",
  "Transparent reporting for your ESG goals.",
  "Tailored solutions for MSPs, schools, and corporate offices.",
];

export default function WhyUs() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="why-us" ref={ref} className="relative bg-slate-50 py-24 sm:py-32" data-testid="why-us-section">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative"
        >
          <div className="relative overflow-hidden rounded-2xl border border-slate-200 shadow-[0_32px_64px_rgba(4,10,7,0.14)]">
            <motion.img
              src="/images/why-us-laptops.png"
              alt="Refurbished corporate laptops stacked beside a locked security bin"
              style={{ y: imageY, scale: 1.15 }}
              className="h-[24rem] w-full object-cover sm:h-[30rem]"
              data-testid="why-us-image"
            />
          </div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
            className="absolute -bottom-6 -right-3 flex items-center gap-3 rounded-xl border border-slate-200 bg-white/90 p-4 shadow-xl backdrop-blur-xl sm:-right-6"
            data-testid="why-us-badge"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-navy">
              <Lock className="h-5 w-5 text-eco" />
            </span>
            <div>
              <p className="text-sm font-bold text-navy">Chain of custody</p>
              <p className="text-xs text-slate-500">Tracked from door to destruction</p>
            </div>
          </motion.div>
        </motion.div>

        <div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-eco-dark" data-testid="why-us-overline">
              Chapter 03 — Why Revanta
            </p>
            <h2 className="font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl" data-testid="why-us-heading">
              Compliance isn't a feature.
              <br />
              <span className="text-slate-400">It's the whole product.</span>
            </h2>
          </motion.div>

          <ul className="mt-10 flex flex-col gap-5" data-testid="why-us-points">
            {POINTS.map((point, i) => (
              <motion.li
                key={point}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, ease: EASE, delay: 0.1 * i }}
                className="group flex items-start gap-4 rounded-xl border border-transparent p-3 transition-[border-color,background-color] duration-300 hover:border-slate-200 hover:bg-white"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-eco/10 transition-colors duration-300 group-hover:bg-eco">
                  <CheckCircle2 className="h-4 w-4 text-eco-dark transition-colors duration-300 group-hover:text-white" strokeWidth={2.4} />
                </span>
                <span className="text-base font-medium leading-relaxed text-slate-700">{point}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
